import { Component, computed, inject, OnInit } from '@angular/core';
import { GuiaComponent } from '../../../../shared/components/guia-component/guia-component';
import { Router } from '@angular/router';
import { ResultStateService } from '../../services/result.state.service';
import { AnswerResponseDto } from '../../interface/survey-responses.interface';
import { EmptyStateComponent } from '@/app/shared/components/empty-state/empty-state.component';
import { Resultado } from '../../interface/result.inteface';
import { ThemeService } from '@/app/core/services/theme/theme.service';

@Component({
  selector: 'app-guia3-component',
  standalone: true,
  imports: [GuiaComponent, EmptyStateComponent],
  templateUrl: './guia3-component.html',
  styleUrl: './guia3-component.scss'
})
export class Guia3Component implements OnInit {
  private resultStateService = inject(ResultStateService);
  private themeService = inject(ThemeService);
  private router = inject(Router);
  private cuestionario!: Resultado | null;

  protected GUIA_KEY = "GUIA III"
  protected themeContent = this.themeService.themeContent;
  protected guideThreeComponent = computed(() => this.themeContent()?.guideThreeComponent);

  guia3: any[] = [];

  ngOnInit(): void {
    this.cuestionario = this.resultStateService.getCuestionario();

    // Obtener la data real de las respuestas para esta guía específica
    if (this.cuestionario) {
      const surveyAnswers = this.resultStateService.getSurveyAnswersForGuide(this.cuestionario.token, this.cuestionario.guideId!);

      // Si no hay data real, usar mock data como fallback
      if (surveyAnswers && surveyAnswers.length > 0) {
        this.guia3 = this.transformSurveyAnswersToGuiaFormat(surveyAnswers);
        this.guia3.sort((a, b) => a.category.localeCompare(b.category, 'es', { sensitivity: 'base' }));
      }
    }
  }

  handleGoBack(){
    this.router.navigate(['resultados']);
  }

  /**
   * Transforma las respuestas del API al formato que espera GuiaComponent
   */
  private transformSurveyAnswersToGuiaFormat(answers: AnswerResponseDto[]): any[] {

    return answers.map(answer => {

      // Buscar la pregunta correspondiente en el mock data para mantener la estructura
      const mockQuestion = this.getMockQuestionByCode(answer.questionCode);

      const transformedAnswer = {
        questionId: answer.questionCode,
        category: mockQuestion?.category,
        domain: mockQuestion?.domain,
        dimension: mockQuestion?.dimension,
        score: Number(answer.responseValue) || 0,
        inverted: mockQuestion?.inverted,
        text: mockQuestion?.text
      };

      return transformedAnswer;
    });
  }

  /**
   * Busca la pregunta correspondiente en el mock data por questionCode
   */
  private getMockQuestionByCode(questionCode: string): any {
    const mockData = [
      {
        "questionId": "GIII_1",
        "category": "Ambiente de trabajo",
        "domain": "Condiciones en el ambiente de trabajo",
        "dimension": "Condiciones peligrosas e inseguras",
        "score": 4,
        "inverted": true,
        "text": "El espacio donde trabajo me permite realizar mis actividades de manera segura e higiénica"
      },
      {
        "questionId": "GIII_2",
        "category": "Ambiente de trabajo",
        "domain": "Condiciones en el ambiente de trabajo",
        "dimension": "Condiciones deficientes e insalubres",
        "score": 4,
        "inverted": false,
        "text": "Mi trabajo me exige hacer mucho esfuerzo físico"
      },
      {
        "questionId": "GIII_3",
        "category": "Ambiente de trabajo",
        "domain": "Condiciones en el ambiente de trabajo",
        "dimension": "Condiciones peligrosas e inseguras",
        "score": 4,
        "inverted": false,
        "text": "Me preocupa sufrir un accidente en mi trabajo"
      },
      {
        "questionId": "GIII_4",
        "category": "Ambiente de trabajo",
        "domain": "Condiciones en el ambiente de trabajo",
        "dimension": "Condiciones deficientes e insalubres",
        "score": 4,
        "inverted": true,
        "text": "Considero que en mi trabajo se aplican las normas de seguridad y salud en el trabajo"
      },
      {
        "questionId": "GIII_5",
        "category": "Ambiente de trabajo",
        "domain": "Condiciones en el ambiente de trabajo",
        "dimension": "Trabajos peligrosos",
        "score": 4,
        "inverted": false,
        "text": "Considero que las actividades que realizo son peligrosas"
      },
      {
        "questionId": "GIII_6",
        "category": "Factores propios de la actividad",
        "domain": "Carga de trabajo",
        "dimension": "Cargas cuantitativas",
        "score": 4,
        "inverted": false,
        "text": "Por la cantidad de trabajo que tengo debo quedarme tiempo adicional a mi turno"
      },
      {
        "questionId": "GIII_7",
        "category": "Factores propios de la actividad",
        "domain": "Carga de trabajo",
        "dimension": "Ritmos de trabajo acelerado",
        "score": 4,
        "inverted": false,
        "text": "Por la cantidad de trabajo que tengo debo trabajar sin parar"
      },
      {
        "questionId": "GIII_8",
        "category": "Factores propios de la actividad",
        "domain": "Carga de trabajo",
        "dimension": "Ritmos de trabajo acelerado",
        "score": 4,
        "inverted": false,
        "text": "Considero que es necesario mantener un ritmo de trabajo acelerado"
      },
      {
        "questionId": "GIII_9",
        "category": "Factores propios de la actividad",
        "domain": "Carga de trabajo",
        "dimension": "Carga mental",
        "score": 4,
        "inverted": false,
        "text": "Mi trabajo exige que esté muy concentrado"
      },
      {
        "questionId": "GIII_10",
        "category": "Factores propios de la actividad",
        "domain": "Carga de trabajo",
        "dimension": "Carga mental",
        "score": 4,
        "inverted": false,
        "text": "Mi trabajo requiere que memorice mucha información"
      },
      {
        "questionId": "GIII_11",
        "category": "Factores propios de la actividad",
        "domain": "Carga de trabajo",
        "dimension": "Carga mental",
        "score": 4,
        "inverted": false,
        "text": "En mi trabajo tengo que tomar decisiones difíciles muy rápido"
      },
      {
        "questionId": "GIII_12",
        "category": "Factores propios de la actividad",
        "domain": "Carga de trabajo",
        "dimension": "Cargas cuantitativas",
        "score": 4,
        "inverted": false,
        "text": "Mi trabajo exige que atienda varios asuntos al mismo tiempo"
      },
      {
        "questionId": "GIII_13",
        "category": "Factores propios de la actividad",
        "domain": "Carga de trabajo",
        "dimension": "Cargas de alta responsabilidad",
        "score": 4,
        "inverted": false,
        "text": "En mi trabajo soy responsable de cosas de mucho valor"
      },
      {
        "questionId": "GIII_14",
        "category": "Factores propios de la actividad",
        "domain": "Carga de trabajo",
        "dimension": "Cargas de alta responsabilidad",
        "score": 4,
        "inverted": false,
        "text": "Respondo ante mi jefe por los resultados de toda mi área de trabajo"
      },
      {
        "questionId": "GIII_15",
        "category": "Factores propios de la actividad",
        "domain": "Carga de trabajo",
        "dimension": "Cargas contradictorias o inconsistentes",
        "score": 4,
        "inverted": false,
        "text": "En el trabajo me dan órdenes contradictorias"
      },
      {
        "questionId": "GIII_16",
        "category": "Factores propios de la actividad",
        "domain": "Carga de trabajo",
        "dimension": "Cargas contradictorias o inconsistentes",
        "score": 4,
        "inverted": false,
        "text": "Considero que en mi trabajo me piden hacer cosas innecesarias"
      },
      {
        "questionId": "GIII_17",
        "category": "Organización del tiempo de trabajo",
        "domain": "Jornada de trabajo",
        "dimension": "Jornadas de trabajo extensas",
        "score": 4,
        "inverted": false,
        "text": "Trabajo horas extras más de tres veces a la semana"
      },
      {
        "questionId": "GIII_18",
        "category": "Organización del tiempo de trabajo",
        "domain": "Jornada de trabajo",
        "dimension": "Jornadas de trabajo extensas",
        "score": 4,
        "inverted": false,
        "text": "Mi trabajo me exige laborar en días de descanso, festivos o fines de semana"
      },
      {
        "questionId": "GIII_19",
        "category": "Organización del tiempo de trabajo",
        "domain": "Interferencia en la relación trabajo-familia",
        "dimension": "Influencia del trabajo fuera del centro laboral",
        "score": 4,
        "inverted": false,
        "text": "Considero que el tiempo en el trabajo es mucho y perjudica mis actividades familiares o personales"
      },
      {
        "questionId": "GIII_20",
        "category": "Organización del tiempo de trabajo",
        "domain": "Interferencia en la relación trabajo-familia",
        "dimension": "Influencia del trabajo fuera del centro laboral",
        "score": 4,
        "inverted": false,
        "text": "Debo atender asuntos de trabajo cuando estoy en casa"
      },
      {
        "questionId": "GIII_21",
        "category": "Organización del tiempo de trabajo",
        "domain": "Interferencia en la relación trabajo-familia",
        "dimension": "Influencia de las responsabilidades familiares",
        "score": 4,
        "inverted": false,
        "text": "Pienso en las actividades familiares o personales cuando estoy en mi trabajo"
      },
      {
        "questionId": "GIII_22",
        "category": "Organización del tiempo de trabajo",
        "domain": "Interferencia en la relación trabajo-familia",
        "dimension": "Influencia de las responsabilidades familiares",
        "score": 4,
        "inverted": false,
        "text": "Pienso que mis responsabilidades familiares afectan mi trabajo"
      },
      {
        "questionId": "GIII_23",
        "category": "Factores propios de la actividad",
        "domain": "Falta de control sobre el trabajo",
        "dimension": "Limitada o nula posibilidad de desarrollo",
        "score": 4,
        "inverted": true,
        "text": "Mi trabajo permite que desarrolle nuevas habilidades"
      },
      {
        "questionId": "GIII_24",
        "category": "Factores propios de la actividad",
        "domain": "Falta de control sobre el trabajo",
        "dimension": "Limitada o nula posibilidad de desarrollo",
        "score": 4,
        "inverted": true,
        "text": "En mi trabajo puedo aspirar a un mejor puesto"
      },
      {
        "questionId": "GIII_25",
        "category": "Factores propios de la actividad",
        "domain": "Falta de control sobre el trabajo",
        "dimension": "Falta de control y autonomía sobre el trabajo",
        "score": 4,
        "inverted": true,
        "text": "Durante mi jornada de trabajo puedo tomar pausas cuando las necesito"
      },
      {
        "questionId": "GIII_26",
        "category": "Factores propios de la actividad",
        "domain": "Falta de control sobre el trabajo",
        "dimension": "Falta de control y autonomía sobre el trabajo",
        "score": 4,
        "inverted": true,
        "text": "Puedo decidir cuánto trabajo realizo durante la jornada laboral"
      },
      {
        "questionId": "GIII_27",
        "category": "Factores propios de la actividad",
        "domain": "Falta de control sobre el trabajo",
        "dimension": "Falta de control y autonomía sobre el trabajo",
        "score": 4,
        "inverted": true,
        "text": "Puedo decidir la velocidad a la que realizo mis actividades en mi trabajo"
      },
      {
        "questionId": "GIII_28",
        "category": "Factores propios de la actividad",
        "domain": "Falta de control sobre el trabajo",
        "dimension": "Falta de control y autonomía sobre el trabajo",
        "score": 4,
        "inverted": true,
        "text": "Puedo cambiar el orden de las actividades que realizo en mi trabajo"
      },
      {
        "questionId": "GIII_29",
        "category": "Factores propios de la actividad",
        "domain": "Falta de control sobre el trabajo",
        "dimension": "Insuficiente participación y manejo del cambio",
        "score": 4,
        "inverted": false,
        "text": "Los cambios que se presentan en mi trabajo dificultan mi labor"
      },
      {
        "questionId": "GIII_30",
        "category": "Factores propios de la actividad",
        "domain": "Falta de control sobre el trabajo",
        "dimension": "Insuficiente participación y manejo del cambio",
        "score": 4,
        "inverted": true,
        "text": "Cuando se presentan cambios en mi trabajo se tienen en cuenta mis ideas o aportaciones"
      },
      {
        "questionId": "GIII_31",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Liderazgo",
        "dimension": "Escasa claridad de funciones",
        "score": 4,
        "inverted": true,
        "text": "Me informan con claridad cuáles son mis funciones"
      },
      {
        "questionId": "GIII_32",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Liderazgo",
        "dimension": "Escasa claridad de funciones",
        "score": 4,
        "inverted": true,
        "text": "Me explican claramente los resultados que debo obtener en mi trabajo"
      },
      {
        "questionId": "GIII_33",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Liderazgo",
        "dimension": "Escasa claridad de funciones",
        "score": 4,
        "inverted": true,
        "text": "Me explican claramente los objetivos de mi trabajo"
      },
      {
        "questionId": "GIII_34",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Liderazgo",
        "dimension": "Escasa claridad de funciones",
        "score": 4,
        "inverted": true,
        "text": "Me informan con quién puedo resolver problemas o asuntos de trabajo"
      },
      {
        "questionId": "GIII_35",
        "category": "Factores propios de la actividad",
        "domain": "Falta de control sobre el trabajo",
        "dimension": "Limitada o inexistente capacitación",
        "score": 4,
        "inverted": true,
        "text": "Me permiten asistir a capacitaciones relacionadas con mi trabajo"
      },
      {
        "questionId": "GIII_36",
        "category": "Factores propios de la actividad",
        "domain": "Falta de control sobre el trabajo",
        "dimension": "Limitada o inexistente capacitación",
        "score": 4,
        "inverted": true,
        "text": "Recibo capacitación útil para hacer mi trabajo"
      },
      {
        "questionId": "GIII_37",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Liderazgo",
        "dimension": "Características del liderazgo",
        "score": 4,
        "inverted": true,
        "text": "Mi jefe ayuda a organizar mejor el trabajo"
      },
      {
        "questionId": "GIII_38",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Liderazgo",
        "dimension": "Características del liderazgo",
        "score": 4,
        "inverted": true,
        "text": "Mi jefe tiene en cuenta mis puntos de vista y opiniones"
      },
      {
        "questionId": "GIII_39",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Liderazgo",
        "dimension": "Características del liderazgo",
        "score": 4,
        "inverted": true,
        "text": "Mi jefe me comunica a tiempo la información relacionada con el trabajo"
      },
      {
        "questionId": "GIII_40",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Liderazgo",
        "dimension": "Características del liderazgo",
        "score": 4,
        "inverted": true,
        "text": "La orientación que me da mi jefe me ayuda a realizar mejor mi trabajo"
      },
      {
        "questionId": "GIII_41",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Liderazgo",
        "dimension": "Características del liderazgo",
        "score": 4,
        "inverted": true,
        "text": "Mi jefe ayuda a solucionar los problemas que se presentan en el trabajo"
      },
      {
        "questionId": "GIII_42",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Relaciones en el trabajo",
        "dimension": "Relaciones sociales en el trabajo",
        "score": 4,
        "inverted": true,
        "text": "Puedo confiar en mis compañeros de trabajo"
      },
      {
        "questionId": "GIII_43",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Relaciones en el trabajo",
        "dimension": "Relaciones sociales en el trabajo",
        "score": 4,
        "inverted": true,
        "text": "Entre compañeros solucionamos los problemas de trabajo de forma respetuosa"
      },
      {
        "questionId": "GIII_44",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Relaciones en el trabajo",
        "dimension": "Relaciones sociales en el trabajo",
        "score": 4,
        "inverted": true,
        "text": "En mi trabajo me hacen sentir parte del grupo"
      },
      {
        "questionId": "GIII_45",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Relaciones en el trabajo",
        "dimension": "Relaciones sociales en el trabajo",
        "score": 4,
        "inverted": true,
        "text": "Cuando tenemos que realizar trabajo de equipo los compañeros colaboran"
      },
      {
        "questionId": "GIII_46",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Relaciones en el trabajo",
        "dimension": "Relaciones sociales en el trabajo",
        "score": 4,
        "inverted": true,
        "text": "Mis compañeros de trabajo me ayudan cuando tengo dificultades"
      },
      {
        "questionId": "GIII_47",
        "category": "Entorno organizacional",
        "domain": "Reconocimiento del desempeño",
        "dimension": "Escasa o nula retroalimentación del desempeño",
        "score": 4,
        "inverted": true,
        "text": "Me informan sobre lo que hago bien en mi trabajo"
      },
      {
        "questionId": "GIII_48",
        "category": "Entorno organizacional",
        "domain": "Reconocimiento del desempeño",
        "dimension": "Escasa o nula retroalimentación del desempeño",
        "score": 4,
        "inverted": true,
        "text": "La forma como evalúan mi trabajo en mi centro de trabajo me ayuda a mejorar mi desempeño"
      },
      {
        "questionId": "GIII_49",
        "category": "Entorno organizacional",
        "domain": "Reconocimiento del desempeño",
        "dimension": "Escaso o nulo reconocimiento y compensación",
        "score": 4,
        "inverted": true,
        "text": "En mi centro de trabajo me pagan a tiempo mi salario"
      },
      {
        "questionId": "GIII_50",
        "category": "Entorno organizacional",
        "domain": "Reconocimiento del desempeño",
        "dimension": "Escaso o nulo reconocimiento y compensación",
        "score": 4,
        "inverted": true,
        "text": "El pago que recibo es el que merezco por el trabajo que realizo"
      },
      {
        "questionId": "GIII_51",
        "category": "Entorno organizacional",
        "domain": "Reconocimiento del desempeño",
        "dimension": "Escaso o nulo reconocimiento y compensación",
        "score": 4,
        "inverted": true,
        "text": "Si obtengo los resultados esperados en mi trabajo me recompensan o reconocen"
      },
      {
        "questionId": "GIII_52",
        "category": "Entorno organizacional",
        "domain": "Reconocimiento del desempeño",
        "dimension": "Escaso o nulo reconocimiento y compensación",
        "score": 4,
        "inverted": true,
        "text": "Las personas que hacen bien el trabajo pueden crecer laboralmente"
      },
      {
        "questionId": "GIII_53",
        "category": "Entorno organizacional",
        "domain": "Insuficiente sentido de pertenencia e inestabilidad",
        "dimension": "Inestabilidad laboral",
        "score": 4,
        "inverted": true,
        "text": "Considero que mi trabajo es estable"
      },
      {
        "questionId": "GIII_54",
        "category": "Entorno organizacional",
        "domain": "Insuficiente sentido de pertenencia e inestabilidad",
        "dimension": "Inestabilidad laboral",
        "score": 4,
        "inverted": false,
        "text": "En mi trabajo existe continua rotación de personal"
      },
      {
        "questionId": "GIII_55",
        "category": "Entorno organizacional",
        "domain": "Insuficiente sentido de pertenencia e inestabilidad",
        "dimension": "Limitado sentido de pertenencia",
        "score": 4,
        "inverted": true,
        "text": "Siento orgullo de laborar en este centro de trabajo"
      },
      {
        "questionId": "GIII_56",
        "category": "Entorno organizacional",
        "domain": "Insuficiente sentido de pertenencia e inestabilidad",
        "dimension": "Limitado sentido de pertenencia",
        "score": 4,
        "inverted": true,
        "text": "Me siento comprometido con mi trabajo"
      },
      {
        "questionId": "GIII_57",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Violencia",
        "dimension": "Violencia laboral",
        "score": 4,
        "inverted": true,
        "text": "En mi trabajo puedo expresarme libremente sin interrupciones"
      },
      {
        "questionId": "GIII_58",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Violencia",
        "dimension": "Violencia laboral",
        "score": 4,
        "inverted": false,
        "text": "Recibo críticas constantes a mi persona y/o trabajo"
      },
      {
        "questionId": "GIII_59",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Violencia",
        "dimension": "Violencia laboral",
        "score": 4,
        "inverted": false,
        "text": "Recibo burlas, calumnias, difamaciones, humillaciones o ridiculizaciones"
      },
      {
        "questionId": "GIII_60",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Violencia",
        "dimension": "Violencia laboral",
        "score": 4,
        "inverted": false,
        "text": "Se ignora mi presencia o se me excluye de las reuniones de trabajo y en la toma de decisiones"
      },
      {
        "questionId": "GIII_61",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Violencia",
        "dimension": "Violencia laboral",
        "score": 4,
        "inverted": false,
        "text": "Se manipulan las situaciones de trabajo para hacerme parecer un mal trabajador"
      },
      {
        "questionId": "GIII_62",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Violencia",
        "dimension": "Violencia laboral",
        "score": 4,
        "inverted": false,
        "text": "Se ignoran mis éxitos laborales y se atribuyen a otros trabajadores"
      },
      {
        "questionId": "GIII_63",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Violencia",
        "dimension": "Violencia laboral",
        "score": 4,
        "inverted": false,
        "text": "Me bloquean o impiden las oportunidades que tengo para obtener ascenso o mejora en mi trabajo"
      },
      {
        "questionId": "GIII_64",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Violencia",
        "dimension": "Violencia laboral",
        "score": 4,
        "inverted": false,
        "text": "He presenciado actos de violencia en mi centro de trabajo"
      },
      {
        "questionId": "GIII_65",
        "category": "Factores propios de la actividad",
        "domain": "Carga de trabajo",
        "dimension": "Cargas psicológicas emocionales",
        "score": 4,
        "inverted": false,
        "text": "Atiendo clientes o usuarios muy enojados"
      },
      {
        "questionId": "GIII_66",
        "category": "Factores propios de la actividad",
        "domain": "Carga de trabajo",
        "dimension": "Cargas psicológicas emocionales",
        "score": 4,
        "inverted": false,
        "text": "Mi trabajo me exige atender personas muy necesitadas de ayuda o enfermas"
      },
      {
        "questionId": "GIII_67",
        "category": "Factores propios de la actividad",
        "domain": "Carga de trabajo",
        "dimension": "Cargas psicológicas emocionales",
        "score": 4,
        "inverted": false,
        "text": "Para hacer mi trabajo debo demostrar sentimientos distintos a los míos"
      },
      {
        "questionId": "GIII_68",
        "category": "Factores propios de la actividad",
        "domain": "Carga de trabajo",
        "dimension": "Cargas psicológicas emocionales",
        "score": 4,
        "inverted": false,
        "text": "Mi trabajo me exige atender situaciones de violencia"
      },
      {
        "questionId": "GIII_69",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Relaciones en el trabajo",
        "dimension": "Deficiente relación con los colaboradores que supervisa",
        "score": 4,
        "inverted": false,
        "text": "Comunican tarde los asuntos de trabajo"
      },
      {
        "questionId": "GIII_70",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Relaciones en el trabajo",
        "dimension": "Deficiente relación con los colaboradores que supervisa",
        "score": 4,
        "inverted": false,
        "text": "Dificultan el logro de los resultados del trabajo"
      },
      {
        "questionId": "GIII_71",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Relaciones en el trabajo",
        "dimension": "Deficiente relación con los colaboradores que supervisa",
        "score": 4,
        "inverted": false,
        "text": "Cooperan poco cuando se necesita"
      },
      {
        "questionId": "GIII_72",
        "category": "Liderazgo y relaciones en el trabajo",
        "domain": "Relaciones en el trabajo",
        "dimension": "Deficiente relación con los colaboradores que supervisa",
        "score": 4,
        "inverted": false,
        "text": "Ignoran las sugerencias para mejorar su trabajo"
      }
    ];

    return mockData.find(q => q.questionId === questionCode);
  }
}
